import "./styles.css";
import {
  buildTree,
  childrenOf,
  DEFAULT_INTENT_PATH,
  ISLAND_CONFIG_PATH,
  ISLAND_CONFIG_REPO,
  parseIslandMap,
  islandResearch,
  type IslandMap,
  DEFAULT_SPEC_PATH,
  DEFAULT_TREE_CONFIG,
  GitHubClient,
  HttpError,
  isRateLimited,
  isTokenRejected,
  LOCALE_KEY,
  repoFileConfig,
  parseRepoConfig,
  REPO_CONFIG_PATHS,
  parentOf,
  parseRepo,
  seasonOf,
  STATUSES,
  t,
  type GitHubUser,
  type Host,
  type ResearchTree,
  type VersionNode,
  type Status,
  type RepoConfig,
  type RepoConfigWarning,
  type MessageKey,
  type TreeConfig,
} from "@researchtree/core";
import * as THREE from "three";
import { followColorScheme } from "./colorscheme";
import { paintSystemBars } from "./systembars";
import { avatar } from "./avatar";
import { append, clear, h, icon } from "./dom";
import { Panel } from "./panel/panel";
import { applyLocale, localePreference } from "./locale";
import { DEMO_REPO, loadingScreen, loginScreen, messageScreen, repoPicker, settingsDialog } from "./screens";
import { parseSettings, SETTING_PARAMS, settingParams, type UrlSettings } from "./settings-url";
import { renderMarkdown } from "./markdown";
import { share } from "./share";
import { statusLabel } from "./theme";
import { repoBanner, ScrollBanner } from "./scroll-banner";
import { fullRepo, readPlace, shortRepo } from "./url";
import { parseViewState, type ViewState } from "./view-state";
import { loadIntroDocs } from "./panel/intro-docs";
import { loadProfile, type Profile } from "./panel/profile";
import { Kanban } from "./views/kanban";
import { World } from "./views/world";
import { worldMapDialog } from "./views/worldmap";
import { islandIds, islandMetricKeys, rootVersion, seasonLabel } from "./views/layout";
import { INTRO_ID, Tree3D, type Heading, type WorldEntry } from "./views/tree3d";
import type { ViewFilter, ViewOptions } from "./views/view";

const RECENT_KEY = "recentRepos";
const LAST_KEY = "lastRepo";
const metricsKey = (repo: string) => `islandMetrics:${repo}`;
const BOARD_KEY = "board";
const MODE_KEY = "viewMode";
const BRAND_KEY = "brandCollapsed";
const GUIDE_URL = "https://darkpyonix.dev/researchtree/guide/";
const PROJECT_URL = "https://github.com/DarkPyonix/researchtree";

/**
 * The view button cycles island -> flat -> tree -> tree3d -> island. "island" is the first view;
 * "flat" presses it flat; "tree" turns the flat view so versions rise from bottom to top; "tree3d"
 * raises the islands again at that angle.
 */
type ViewMode = "island" | "flat" | "tree" | "tree3d";
const NEXT_MODE: Record<ViewMode, ViewMode> = { island: "flat", flat: "tree", tree: "tree3d", tree3d: "island" };
/** Label of the button in each mode: what pressing it does. */
const MODE_ACTION: Record<ViewMode, MessageKey> = { island: "toolbar.flat", flat: "toolbar.tree", tree: "toolbar.raise", tree3d: "toolbar.island" };
const isFlatMode = (mode: ViewMode) => mode === "flat" || mode === "tree";
const headingOf = (mode: ViewMode): Heading => (mode === "tree" || mode === "tree3d" ? "tree" : "island");

function savedMode(value: unknown): ViewMode {
  if (value === "2d") return "flat"; // saved by earlier versions
  return value === "flat" || value === "tree" || value === "tree3d" ? value : "island";
}

const hint = (mode: ViewMode) => t(isFlatMode(mode) ? "hint.2d" : "hint.3d");

/** Start the viewer on the given host (web / extension / local). */
export async function startApp(host: Host, root: HTMLElement): Promise<void> {
  document.body.classList.add(`host-${host.kind}`);
  // A link may carry settings (`?lang=en&root=…`); the language has to be in place before any screen.
  const urlSettings = parseSettings(location.search);
  if (urlSettings.locale) host.storage.set(LOCALE_KEY, urlSettings.locale);
  applyLocale(host);
  followColorScheme();
  await new App(host, root, urlSettings).boot();
}

function urlParam(name: string): string | null {
  try {
    return new URLSearchParams(location.search).get(name);
  } catch {
    return null;
  }
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

class App {
  private readonly gh: GitHubClient;
  private user: GitHubUser | null = null;
  private tree: ResearchTree | null = null;
  private view: Tree3D | null = null;
  private mode: ViewMode;
  /** The board replaces the island while it is open; both show the same tree and panel. */
  private board: boolean;
  private kanban: Kanban | null = null;
  /** Camera from a saved view, waiting for the 3D view to exist. */
  private restoreCamera: number[] | null = null;
  private banner: ScrollBanner | null = null;
  /** The account map, when the address names one. A null map has not been read from GitHub yet. */
  private world: { user: string; map: IslandMap | null } | null = null;
  private profile: Profile | null = null;
  /** Research on the map this reader cannot open. Checked ahead when signed in; otherwise learned
   * the first time one is opened, so a guest never spends their sixty calls an hour on locked doors. */
  private locked = new Set<string>();
  private config: TreeConfig = DEFAULT_TREE_CONFIG;
  /** `.researchtree` on the root branch of the open repo (shared by the team; empty when absent). */
  private repoFile: { config: RepoConfig; warnings: RepoConfigWarning[] } = { config: {}, warnings: [] };
  /** What the repository says about itself, for the scroll that names it. */
  private repoDescription: string | null = null;
  private stage: { canvas: HTMLElement; hint: HTMLElement; options: ViewOptions; boardInsets: () => { top: number; right: number; bottom: number } } | null = null;
  private panel: Panel | null = null;
  private selected: string | null = null;
  private hidden = new Set<Status>();
  private shell: {
    brand: HTMLElement;
    gens: HTMLElement;
    toast: HTMLElement;
  } | null = null;
  private activeDepth: number | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  /** Registered once, for the browser's back and forward buttons. */
  private backListener: (() => void) | null = null;
  /** Watches the repo card and the panel, the two edges the board is fitted between. */
  private chromeWatch: ResizeObserver | null = null;
  /** Every research of the account, built once and kept: the scene holds all of them at all times. */
  private worldTrees = new Map<string, ResearchTree>();
  private worldEntries: WorldEntry[] = [];
  /** The travel map opens by itself once per visit to an account, not on every redraw. */
  private mapShown = false;
  private timers: number[] = [];

  /** True while the screen is showing an account's map of islands rather than one research. */
  private get isWorld(): boolean {
    return this.world !== null && this.tree === null;
  }

  /**
   * True while the scene holds every island of the account. Reading one research does not empty the
   * sea around it: the reader can drag straight on to the next one. The flat views and the board are
   * about a single tree, so those drop back to one island.
   */
  private get worldScene(): boolean {
    return Boolean(this.world?.map) && !this.board;
  }

  /**
   * What the sea holds right now: every island in the island view, and only the research being read
   * in the flat, tree and 3D views — those are about one tree. The scene is never rebuilt to change
   * this, so the press-down and the turns keep playing across it.
   */
  private sceneEntries(): WorldEntry[] {
    if (this.mode === "island" || !this.tree) return this.worldEntries;
    const mine = this.worldEntries.find((e) => e.tree?.repo === this.tree!.repo);
    return mine ? [mine] : this.worldEntries;
  }

  /** Node ids are scoped by repository while the whole account is on screen. */
  private viewId(id: string | null): string | null {
    if (!id || !this.worldScene || !this.tree) return id;
    return `${this.tree.repo}\u0000${id}`;
  }

  constructor(
    private readonly host: Host,
    private readonly root: HTMLElement,
    /** Settings asked for by the link that opened the viewer; used once, for the repo it opens. */
    private urlSettings: UrlSettings = { ignored: [] },
  ) {
    this.gh = new GitHubClient(host);
    this.mode = savedMode(host.storage.get<string>(MODE_KEY));
    this.board = Boolean(host.storage.get<boolean>(BOARD_KEY));
  }

  private mount(el: HTMLElement): void {
    if (this.keyHandler) document.removeEventListener("keydown", this.keyHandler);
    this.keyHandler = null;
    for (const timer of this.timers) clearTimeout(timer);
    this.timers = [];
    this.root.querySelector(".map-overlay")?.remove();
    this.view?.destroy();
    this.view = null;
    this.kanban?.destroy();
    this.kanban = null;
    this.banner?.destroy();
    this.banner = null;
    this.chromeWatch?.disconnect();
    this.chromeWatch = null;
    this.stage = null;
    this.panel = null;
    this.shell = null;
    clear(this.root);
    this.root.append(el);
    paintSystemBars(false); // full-page screens sit on the page background
  }

  async boot(loginError?: string): Promise<void> {
    this.mount(loadingScreen(t("app.loading")));
    try {
      this.user = await this.host.auth.current();
    } catch (e) {
      this.mount(
        messageScreen({
          title: t("app.connectFailedTitle"),
          body: [errorText(e), t("app.connectFailedBody")],
          actions: [{ label: t("common.retry"), primary: true, onClick: () => void this.boot() }],
        }),
      );
      return;
    }
    this.listenForBack();
    const place = readPlace(location.search);
    if (place.user && !place.repo) return this.showWorld(place.user);
    // An account and a research: that account's island, with this research under the camera. The
    // research may belong to someone else (an organization's repository on a personal island); the
    // island is whose map it is, not who owns the repository.
    if (place.user && place.repo) return this.openPlace(place.user, place.repo);
    if (place.user) this.world = { user: place.user, map: null };
    const saved = place.repo ? null : parseViewState(this.host.capabilities.viewState?.load());
    if (saved) this.applyViewState(saved);
    const repo = place.repo ?? saved?.repo ?? (await this.host.initialRepo()) ?? this.host.storage.get<string>(LAST_KEY) ?? null;
    // Guest mode: a link to a public repo opens read-only, without a sign-in first. GitHub allows
    // 60 anonymous calls an hour per address, so the sign-in screen is still one click away.
    if (!this.user) {
      if (loginError || !repo) return this.showLogin(loginError);
      return this.openRepo(repo);
    }
    if (!repo) return this.showPicker();
    await this.openRepo(repo);
  }

  /** Back and forward: whatever the address now says, that is what goes on screen. */
  private listenForBack(): void {
    if (this.host.kind === "extension" || this.backListener) return;
    this.backListener = () => void this.goto(readPlace(location.search));
    window.addEventListener("popstate", this.backListener);
  }

  private async goto(place: { user: string | null; repo: string | null }): Promise<void> {
    if (place.repo) {
      if (place.user && this.world?.user === place.user && this.worldEntries.length) return void this.focusResearch(place.repo);
      if (place.user) return void this.openPlace(place.user, place.repo);
      this.world = null;
      await this.openRepo(place.repo);
    } else if (place.user) {
      await this.showWorld(place.user);
    } else if (this.user) {
      this.showPicker();
    } else {
      this.showLogin();
    }
  }

  private showLogin(error?: string): void {
    // Without an address bar (the PowerPoint add-in) a guest has no other way in, and a reviewer or
    // a first-time reader can see what the viewer does before signing in.
    const onDemo = this.host.capabilities.viewState ? () => void this.openRepo(DEMO_REPO) : undefined;
    this.mount(loginScreen({ host: this.host, error, onSignedIn: () => void this.boot(), onDemo }));
  }

  private showPicker(error?: string): void {
    if (!this.user) return this.showLogin(error);
    this.mount(
      repoPicker({
        gh: this.gh,
        user: this.user!,
        recent: this.host.storage.get<string[]>(RECENT_KEY) ?? [],
        error,
        onPick: (repo) => void this.openRepo(repo),
        onSignOut: () => void this.signOut(),
      }),
    );
  }

  private async signOut(): Promise<void> {
    await this.host.auth.signOut();
    this.user = null;
    this.setUrl({ repo: null, node: null });
    this.showLogin();
  }

  private handleError(e: unknown, repo: string): void {
    if (isTokenRejected(e)) {
      void this.host.auth.signOut().then(() => this.showLogin(t("app.sessionExpired")));
      return;
    }
    if (!this.user && isRateLimited(e)) {
      this.showLogin(t("guest.rateLimited"));
      return;
    }
    if (e instanceof HttpError && (e.status === 404 || e.status === 403)) {
      // A map may hold research this reader cannot see (a private repository, or someone else's).
      // Losing the whole map over one locked island would be the wrong trade, so we go back to it.
      if (this.world) {
        this.locked.add(repo);
        return this.renderWorld(t("world.locked", { repo }));
      }
      if (!this.user) return this.showLogin(t("guest.needsSignIn", { repo }));
      this.showPicker(t("app.repoNotFound", { repo }));
      return;
    }
    this.mount(
      messageScreen({
        title: t("app.treeFailedTitle"),
        body: [errorText(e)],
        actions: [
          { label: t("common.retry"), primary: true, onClick: () => void this.openRepo(repo) },
          { label: t("common.otherRepo"), onClick: () => this.showPicker() },
        ],
      }),
    );
  }

  private async openRepo(repo: string): Promise<void> {
    if (!parseRepo(repo)) return this.showPicker(t("app.invalidRepo"));
    // Already on the account's sea: sail to the island instead of building a screen for it alone.
    if (this.worldEntries.length && this.stage && this.world?.map) return this.focusResearch(repo);
    this.mount(loadingScreen(t("app.loadingRepo", { repo })));
    // The repository's own file decides the branches, so it is read before anything else.
    const info = await this.gh.repoInfo(repo).catch(() => ({ description: null, defaultBranch: null }));
    this.repoDescription = info.description;
    this.repoFile = await this.readRepoFile(repo, info.defaultBranch).catch(() => ({ config: {}, warnings: [] }));
    this.config = repoFileConfig(this.repoFile.config);
    const { root, prefix } = this.config;
    try {
      if (!(await this.gh.branchExists(repo, root))) {
        this.mount(
          messageScreen({
            eyebrow: repo,
            title: t("app.noRootTitle", { root }),
            body: [
              t("app.noRootBody", { root }),
              h("pre", { class: "code" }, `git switch -c ${root}\ngit push -u origin ${root}`),
              t("app.noRootNext", { prefix }),
            ],
            actions: [
              { label: t("app.recheck"), primary: true, onClick: () => void this.openRepo(repo) },
              { label: t("settings.branchTitle"), onClick: () => this.openSettings(repo) },
              { label: t("common.otherRepo"), onClick: () => this.showPicker() },
            ],
          }),
        );
        return;
      }
      this.tree = await this.loadTree(repo);
    } catch (e) {
      this.handleError(e, repo);
      return;
    }

    this.host.storage.set(LAST_KEY, repo);
    const recent = [repo, ...(this.host.storage.get<string[]>(RECENT_KEY) ?? []).filter((r) => r !== repo)].slice(0, 6);
    this.host.storage.set(RECENT_KEY, recent);

    this.renderMain();
    this.announceRepo(repo);
    const want = urlParam("node");
    const known = want && (this.tree.nodes.has(want) || this.tree.versions.has(want) || want === this.tree.root);
    // Arriving at the research is the move that goes into the history; choosing a node inside it is
    // not, and select() writes the address itself, so the entry has to be made before it runs.
    this.setUrl({ repo, node: known ? want : null, push: true });
    this.select(known ? want : null, false);
    this.setUrl({ repo, node: this.selected });
  }

  /** PRs plus research version tags. A repo without tags (or a failed tag lookup) still gets a tree. */
  /**
   * The account's map: every research it shows, as islands to walk between (docs/ISLAND.md).
   * The map lives in the README of the account's `.researchisland` repository.
   */
  private async showWorld(user: string): Promise<void> {
    if (this.world?.user !== user) this.mapShown = false;
    // The address names the island from the first moment, even while it is still loading or missing.
    this.setUrl({ user, repo: null, node: null, push: true });
    this.mount(loadingScreen(t("app.loading")));
    let text: string | null = null;
    try {
      text = await this.gh.getFileText(`${user}/${ISLAND_CONFIG_REPO}`, ISLAND_CONFIG_PATH, "HEAD");
    } catch (e) {
      if (isTokenRejected(e)) return this.showLogin(t("app.sessionExpired"));
      if (!this.user && isRateLimited(e)) return this.showLogin(t("guest.rateLimited"));
      this.mount(messageScreen({ title: t("world.failed", { error: errorText(e) }), body: [], actions: [{ label: t("common.retry"), primary: true, onClick: () => void this.showWorld(user) }] }));
      return;
    }
    if (text === null) {
      this.mount(
        messageScreen({
          eyebrow: user,
          title: t("world.noSettings", { user }),
          body: [
            t("world.noSettingsBody", { user }),
            t("world.noSettingsHow"),
            h("pre", { class: "code" }, "researchtree island init\nresearchtree island add owner/name"),
          ],
          actions: [
            { label: t("world.share"), primary: true, onClick: (button) => void this.invite(user, button) },
            { label: t("common.retry"), onClick: () => void this.showWorld(user) },
          ],
        }),
      );
      return;
    }
    const map = parseIslandMap(text);
    this.enterWorld(user, map);
    this.renderWorld();
  }

  /** Take up an account's map: its trees are read fresh, and its profile follows on its own. */
  private enterWorld(user: string, map: IslandMap): void {
    this.world = { user, map };
    this.worldTrees = new Map();
    this.worldEntries = [];
    this.profile = null;
    void this.findLocked(user, map);
    // The researcher's own introduction is worth one more call, after the map is on screen.
    void loadProfile(this.gh, user).then((profile) => {
      if (this.world?.user !== user) return;
      this.profile = profile;
      if (this.isWorld && this.shell) this.renderBrand();
    });
  }

  /**
   * A link that names both an account and a research: the sea is drawn first and the research opened
   * on it, so dragging away from what you came to read shows everything else the account has.
   */
  private async openPlace(user: string, repo: string): Promise<void> {
    let map: IslandMap | null = null;
    try {
      const text = await this.gh.getFileText(`${user}/${ISLAND_CONFIG_REPO}`, ISLAND_CONFIG_PATH, "HEAD");
      map = text === null ? null : parseIslandMap(text);
    } catch {
      // No map, or it cannot be read: the research still opens, on its own.
    }
    if (!map || !islandResearch(map).some((r) => r.repo === repo)) {
      this.world = { user, map };
      return this.openRepo(repo);
    }
    this.enterWorld(user, map);
    this.mapShown = true; // arriving at a research: the map waits until it is asked for
    this.tree = null;
    this.renderMain();
    void this.loadWorldTrees(map);
    await this.focusResearch(repo);
  }

  /** The account's own island. Already standing on it, the map is simply drawn again. */
  private openIsland(user: string): void {
    if (!user) return;
    if (this.world?.user === user) this.renderWorld();
    else void this.showWorld(user);
  }

  /**
   * Invite a researcher who has no island yet. On a phone that is the system share sheet; on a desktop
   * the invitation lands on the clipboard instead, and the button says so for a moment.
   */
  private async invite(user: string, button: HTMLButtonElement): Promise<void> {
    const url = `${location.origin}${location.pathname}?user=${encodeURIComponent(user)}`;
    const result = await share({ title: t("share.inviteTitle"), text: t("share.inviteText", { user, guide: GUIDE_URL }), url });
    if (result === "shared") return;
    button.dataset.label ??= button.textContent ?? "";
    button.textContent = t(result === "copied" ? "world.shareCopied" : "world.shareFailed");
    window.setTimeout(() => {
      button.textContent = button.dataset.label ?? "";
    }, 2600);
  }

  /**
   * Which research on the map this reader cannot open. Signed in, every repository is asked about
   * once, so the locked ones are drawn locked before anyone clicks. A guest is not asked at all:
   * sixty calls an hour are better spent on the research they can actually read.
   */
  private async findLocked(user: string, map: IslandMap): Promise<void> {
    if (!this.user) return;
    for (const research of islandResearch(map)) {
      if (this.locked.has(research.repo)) continue;
      const info = await this.gh.repoInfo(research.repo).catch(() => null);
      if (info?.defaultBranch) continue;
      // The scene already draws it locked; the map's pins read this set when it opens.
      this.locked.add(research.repo);
    }
  }

  /**
   * The account's map, drawn as the viewer proper: the same toolbar, cards and panel as a research,
   * with every island of the account on one sea. The map dialog is only the way to travel between
   * them quickly; the islands themselves are always there to drag to (docs/ISLAND.md).
   */
  private renderWorld(message?: string): void {
    const world = this.world;
    if (!world) return;
    // Arriving straight at a research (?user=…&repo=…) means the map itself was never read.
    if (!world.map) return void this.showWorld(world.user);
    this.tree = null;
    this.setUrl({ repo: null, node: null });
    this.renderMain();
    if (message) this.toast(message);
    this.banner?.show({ eyebrow: t("world.land"), title: t("world.title", { user: world.user }), kind: "island" });
    if (!islandResearch(world.map).length) {
      this.stage?.canvas.append(h("p", { class: "world-empty muted" }, t("world.empty")));
      return;
    }
    // On arrival the map opens by itself, once — after the islands are on the sea, because the map
    // is a picture of them.
    void this.loadWorldTrees(world.map).then(() => {
      if (this.mapShown || !this.isWorld) return;
      this.mapShown = true;
      this.openMap();
    });
  }

  /** The travel map over the sea. Closing it leaves the islands exactly where they were. */
  private openMap(): void {
    const world = this.world;
    if (!world?.map || !this.shell) return;
    const spacing = 150;
    const places = world.map.islands.flatMap((land) =>
      land.repos.map((entry) => {
        const at = this.mapPlace(world.map!, entry, spacing);
        return { repo: entry.repo, land: land.name, at: { x: at.x, z: at.z }, locked: this.locked.has(entry.repo) };
      }),
    );
    const shot = this.view?.mapSnapshot() ?? null;
    const overlay = worldMapDialog({
      user: world.user,
      shot,
      places,
      here: this.tree?.repo ?? null,
      onSail: (repo) => this.view?.sailTo(repo),
      onOpen: (repo) => void this.openRepo(repo),
    });
    this.root.append(overlay);
  }

  /**
   * A click on the sea: a plant or a stone of another island moves the reader there, one of the
   * island they are already reading selects that node. Nothing is rebuilt either way.
   */
  private onWorldSelect(id: string | null): void {
    if (!id) return this.select(null);
    const [repo, node] = id.split("\u0000");
    if (!repo?.includes("/")) return;
    if (this.tree?.repo === repo) return this.select(node || null);
    void this.focusResearch(repo, node || null);
  }

  /**
   * Read one research without leaving the account's sea: the camera sails to that island, the cards
   * and the panel become its own, and every other island stays where it was, a drag away.
   */
  private async focusResearch(repo: string, node: string | null = null, how: { sail?: boolean; push?: boolean } = {}): Promise<void> {
    const tree = this.worldTrees.get(repo) ?? (await this.loadTree(repo).catch(() => null));
    if (!tree) {
      this.locked.add(repo);
      this.toast(t("world.locked", { repo }));
      return;
    }
    this.worldTrees.set(repo, tree);
    this.tree = tree;
    // The repository's own settings decide its branches and its documents.
    const info = await this.gh.repoInfo(repo).catch(() => ({ description: null, defaultBranch: null }));
    this.repoDescription = info.description;
    this.repoFile = await this.readRepoFile(repo, info.defaultBranch).catch(() => ({ config: {}, warnings: [] }));
    this.config = repoFileConfig(this.repoFile.config);
    this.host.storage.set(LAST_KEY, repo);
    this.refreshChrome();
    this.setUrl({ repo, node, push: how.push ?? true });
    this.announceRepo(repo);
    this.view?.scopeLabels(repo);
    if (how.sail ?? true) this.view?.sailTo(repo);
    this.select(node && (tree.nodes.has(node) || tree.versions.has(node) || node === tree.root) ? node : null, false);
  }

  /**
   * The camera drifted onto an island: reading follows the sailing, so the address and the cards
   * become that research without anyone clicking. The camera is already there, so it is not moved,
   * and the address is rewritten rather than pushed — a drag would otherwise fill the history.
   */
  private onSailedOver(repo: string | null): void {
    if (!repo || !this.world?.map || repo === this.tree?.repo) return;
    if (this.locked.has(repo)) return;
    void this.focusResearch(repo, null, { sail: false, push: false });
  }

  /** Redraw the parts around the scene — the toolbar, the card, the generation bar — in place. */
  private refreshChrome(): void {
    const shellEl = this.stage?.canvas.parentElement;
    shellEl?.querySelector(".toolbar")?.replaceWith(this.renderToolbar());
    this.shell?.brand.classList.remove("world-card");
    this.renderBrand();
    this.renderGenerations();
  }

  /**
   * Every research on the map, read and built into one scene. They arrive one at a time, so the
   * first island is on screen while the rest are still loading.
   */
  private async loadWorldTrees(map: IslandMap): Promise<void> {
    const entries: WorldEntry[] = [];
    const research = islandResearch(map);
    const spacing = 150;
    for (const [i, item] of research.entries()) {
      const at = this.mapPlace(map, item, spacing);
      let tree: ResearchTree;
      try {
        const config = repoFileConfig({ root: item.root, prefix: item.prefix });
        const [prs, tags] = await Promise.all([
          this.gh.listPulls(item.repo),
          this.gh.listVersionTags(item.repo, config.root).catch(() => []),
        ]);
        tree = buildTree(prs, item.repo, config, tags, new Map());
      } catch {
        // Private, or someone else's: the island still belongs on the map, drawn as locked.
        this.locked.add(item.repo);
        entries.push({ locked: item.repo, offset: at });
        this.view?.renderWorld(entries);
        continue;
      }
      this.worldTrees.set(item.repo, tree);
      entries.push({ tree, filter: { hidden: new Set(), metrics: new Map() }, offset: at });
      this.worldEntries = entries;
      if (!this.view || this.world === null) return;
      this.view.renderWorld(entries);
      this.view.scopeLabels(this.tree?.repo ?? null);
      // Reading a research already: hold the camera on its island while the others arrive.
      if (this.tree) this.view.sailTo(this.tree.repo);
      else if (i === 0) this.view.fit(undefined, false);
    }
    this.worldEntries = entries;
    if (this.tree) this.view?.sailTo(this.tree.repo);
    else this.view?.fit(undefined, false);
  }

  /** Where one research's island sits on the sea: its place in its land, and the land's on the map. */
  private mapPlace(map: IslandMap, item: { repo: string }, spacing: number): THREE.Vector3 {
    for (const land of map.islands) {
      const at = land.repos.find((r) => r.repo === item.repo);
      if (!at) continue;
      return new THREE.Vector3(land.at[0] * spacing * 2.4 + at.at[0] * spacing, 0, land.at[1] * spacing * 2.4 + at.at[1] * spacing);
    }
    return new THREE.Vector3();
  }

  /** Who the map belongs to: their own introduction, and their résumé when they published one. */
  private worldCard(user: string): HTMLElement {
    const profile = this.profile;
    const open = () => this.openProfile(user);
    return h(
      "section",
      { class: "card brand world-card" },
      h("div", { class: "eyebrow" }, t("world.land")),
      h("h1", { class: "world-user" }, user),
      h(
        "div",
        { class: "row" },
        profile?.text ? h("button", { class: "btn small", type: "button", onclick: open }, t("profile.open")) : null,
        profile?.resumeUrl
          ? h("button", { class: "btn small", type: "button", onclick: () => this.host.openExternal(profile.resumeUrl!) }, icon("external", 13), ` ${t("profile.resume")}`)
          : null,
      ),
    );
  }

  /** Whose map this is, in the place the repository card would be. */
  private renderWorldCard(brand: HTMLElement): void {
    const user = this.world!.user;
    const profile = this.profile;
    brand.classList.add("world-card");
    append(brand, [
      h("div", { class: "eyebrow" }, t("world.land")),
      h("h1", { class: "world-user" }, user),
      h(
        "div",
        { class: "row" },
        h("button", { class: "btn small", type: "button", onclick: () => this.openMap() }, icon("island", 13), ` ${t("world.map")}`),
        profile?.text ? h("button", { class: "btn small", type: "button", onclick: () => this.openProfile(user) }, t("profile.open")) : null,
        profile?.resumeUrl
          ? h("button", { class: "btn small", type: "button", onclick: () => this.host.openExternal(profile.resumeUrl!) }, icon("external", 13), ` ${t("profile.resume")}`)
          : null,
      ),
    ]);
  }

  private openProfile(user: string): void {
    const profile = this.profile;
    if (!profile?.text) return;
    const body = h("div", { class: "prose" });
    body.append(renderMarkdown(profile.text));
    const dialog = h(
      "div",
      { class: "overlay", onclick: (e: Event) => e.target === dialog && dialog.remove() },
      h(
        "div",
        { class: "card dialog profile-dialog", role: "dialog", "aria-modal": "true", "aria-label": t("profile.title", { user }) },
        h(
          "div",
          { class: "panel-top" },
          h("div", { class: "eyebrow accent" }, t(profile.source === "profile" ? "profile.fromSettings" : "profile.fromAccount")),
          h("button", { class: "icon-btn", "aria-label": t("common.close"), onclick: () => dialog.remove() }, icon("close")),
        ),
        h("h2", { class: "screen-title" }, t("profile.title", { user })),
        body,
      ),
    );
    document.body.append(dialog);
  }

  /** What this research is, from the repository's own documents (docs/ISLAND.md). */
  private showIntro(tree: ResearchTree): void {
    const panel = this.panel;
    if (!panel) return;
    panel.showIntro(tree, { entries: [] });
    const paths = { spec: this.repoFile.config.spec ?? DEFAULT_SPEC_PATH, intent: this.repoFile.config.intent ?? DEFAULT_INTENT_PATH };
    void loadIntroDocs(this.gh, tree, paths).then((docs) => {
      if (this.tree === tree) panel.showIntro(tree, docs);
    });
  }

  /** The scroll that names the research you just walked into (docs/ISLAND.md). */
  private announceRepo(repo: string): void {
    this.banner?.show(repoBanner(repo, this.repoDescription, t("banner.research")));
  }

  private async loadTree(repo: string): Promise<ResearchTree> {
    const [prs, tags, activity] = await Promise.all([
      this.gh.listPulls(repo),
      this.gh.listVersionTags(repo, this.config.root).catch(() => []),
      // Commit dates place experiments on the time axis; without them PR dates are used. They come
      // from the GraphQL API, which turns away anonymous callers, so a guest never has them.
      this.user ? this.gh.listPullActivity(repo).catch(() => new Map()) : Promise.resolve(new Map()),
    ]);
    return buildTree(prs, repo, this.config, tags, activity);
  }

  /**
   * `.researchtree`, which decides the branch names for everyone who opens this repository.
   * It lives on the default branch, so it can name the root branch; older repositories keep it on
   * the root branch instead, and those still work.
   */
  private async readRepoFile(repo: string, defaultBranch: string | null): Promise<{ config: RepoConfig; warnings: RepoConfigWarning[] }> {
    for (const ref of [defaultBranch, DEFAULT_TREE_CONFIG.root].filter((b): b is string => Boolean(b))) {
      for (const path of REPO_CONFIG_PATHS) {
        const text = await this.gh.getFileText(repo, path, ref).catch(() => null);
        if (text !== null) return parseRepoConfig(text);
      }
    }
    return { config: {}, warnings: [] };
  }

  /** Branch settings from the link that opened the viewer, saved for this repo. Applies once. */
  /** Put back the view a saved state describes. The camera is restored once the view exists. */
  private applyViewState(state: ViewState): void {
    this.mode = savedMode(state.mode);
    this.board = state.board;
    this.hidden = new Set(state.hidden);
    this.selected = state.node;
    this.restoreCamera = state.camera.length === 6 ? state.camera : null;
  }

  private viewState(): ViewState | null {
    if (!this.tree) return null;
    return {
      repo: this.tree.repo,
      node: this.selected,
      mode: this.mode,
      board: this.board,
      hidden: [...this.hidden],
      camera: this.board ? [] : [...(this.view?.camera6() ?? [])],
    };
  }

  private async saveViewState(): Promise<void> {
    const state = this.viewState();
    const save = this.host.capabilities.viewState?.save;
    if (!state || !save) return;
    try {
      await save(state);
      this.toast(t("office.viewSaved"));
    } catch (e) {
      this.toast(errorText(e));
    }
  }

  /** What the repo's `.researchtree` decides, shown in the settings dialog. */
  private repoFileNotes(repo: string): string[] {
    if (this.tree?.repo !== repo) return [];
    const notes: string[] = [];
    const { config, warnings } = this.repoFile;
    notes.push(t("config.branches", { root: this.config.root, prefix: this.config.prefix }));
    if (!config.root && !config.prefix) notes.push(t("config.defaults"));
    if (warnings.length) notes.push(t("config.repoWarnings", { items: warnings.map((w) => ("key" in w ? `${w.key} (${w.code})` : w.code)).join(", ") }));
    return notes;
  }

  private openSettings(repo: string): void {
    const dialog = settingsDialog({
      repo,
      notes: this.repoFileNotes(repo),
      locale: localePreference(this.host),
      autoSource: t(this.host.kind === "extension" ? "settings.sourceVscode" : "settings.sourceBrowser"),
      onSave: ({ locale }) => {
        this.host.storage.set(LOCALE_KEY, locale);
        this.setUrl({ repo, node: this.selected });
        // Reloading the repo re-renders every screen in the new language.
        applyLocale(this.host);
        dialog.remove();
        void this.openRepo(repo);
      },
      onCancel: () => dialog.remove(),
    });
    document.body.append(dialog);
    dialog.querySelector("input")?.focus();
  }

  private async refresh(message = t("app.refreshed")): Promise<void> {
    if (!this.tree) return;
    const repo = this.tree.repo;
    this.gh.invalidate();
    try {
      this.tree = await this.loadTree(repo);
    } catch (e) {
      this.toast(t("app.refreshFailed", { error: errorText(e) }));
      return;
    }
    this.renderBrand();
    this.renderGenerations();
    this.current?.render(this.tree, this.filter());
    const sel = this.selected && this.tree.nodes.has(this.selected) ? this.selected : null;
    this.select(sel, false);
    this.toast(message);
  }

  private filter() {
    return { hidden: this.hidden, metrics: this.islandMetrics() };
  }

  /** The view on screen: the board while it is open, otherwise the island. */
  private get current(): { render(tree: ResearchTree, filter: ViewFilter): void; select(id: string | null, focus?: boolean): void } | null {
    return this.board ? this.kanban : this.view;
  }

  private toggleBoard(): void {
    this.board = !this.board;
    this.host.storage.set(BOARD_KEY, this.board || undefined);
    this.renderMain();
    this.select(this.selected, false);
  }

  /** Label metric per island: the saved pick if still recorded there, else the island's most used metric. */
  private islandMetrics(): Map<string, string | null> {
    const tree = this.tree!;
    const saved = this.host.storage.get<Record<string, string>>(metricsKey(tree.repo)) ?? {};
    return new Map(
      islandIds(tree).map((island) => {
        const keys = islandMetricKeys(tree, island);
        const want = saved[island];
        return [island, want === "" ? null : want && keys.includes(want) ? want : (keys[0] ?? null)];
      }),
    );
  }

  private setIslandMetric(island: string, metric: string | null): void {
    const tree = this.tree!;
    const saved = this.host.storage.get<Record<string, string>>(metricsKey(tree.repo)) ?? {};
    this.host.storage.set(metricsKey(tree.repo), { ...saved, [island]: metric ?? "" });
    this.current?.render(tree, this.filter());
    this.current?.select(this.selected, false);
  }

  private renderMain(): void {
    const canvas = h("div", { class: "canvas" });
    const brand = h("section", { class: "card brand", "aria-label": t("app.repoInfo") });
    const time = h("div", { class: "brand-time", "aria-live": "polite" });
    const brandStack = h("div", { class: "brand-stack" }, brand, time);
    const panelEl = h("aside", { class: "card panel", "aria-label": t("app.detail") });
    const gens = h("nav", { class: "card gens", "aria-label": t("gens.nav") });
    const toast = h("div", { class: "toast", role: "status", "aria-live": "polite" });
    const toolbar = this.renderToolbar();
    const hint = h("div", { class: "hint" });

    const shellEl = h("div", { class: "shell" }, canvas, brandStack, toolbar, panelEl, gens, hint, toast);
    this.mount(shellEl);
    this.shell = { brand, gens, toast };
    this.banner = new ScrollBanner(shellEl);

    this.panel = new Panel(panelEl, {
      host: this.host,
      gh: this.gh,
      onNavigate: (id) => this.select(id),
      onUpdated: (message) => this.refresh(message),
      signedIn: () => this.user !== null,
      islandMetric: (island) => ({ keys: islandMetricKeys(this.tree!, island), current: this.islandMetrics().get(island) ?? null }),
      onIslandMetric: (island, metric) => this.setIslandMetric(island, metric),
      specPath: () => this.repoFile.config.spec ?? DEFAULT_SPEC_PATH,
      // Dragging the panel's edge changes how much board is left, so the columns re-fit as it moves.
      onResized: () => this.kanban?.reflow(),
    });
    // The board is fitted between the repo card and the panel, so it re-fits whenever either resizes
    // — collapsing the card, opening the panel, dragging its edge, or the panel settling after a render.
    this.chromeWatch?.disconnect();
    this.chromeWatch = new ResizeObserver(() => this.kanban?.reflow());
    this.chromeWatch.observe(brandStack);
    this.chromeWatch.observe(panelEl);
    // The board is a wide row of columns, so it always starts below the repo card rather than beside it.
    // These are the edges the board may not cross; the gap it keeps from them is the board's own.
    const boardInsets = () => {
      const b = brandStack.getBoundingClientRect();
      const panel = this.panel?.takes ?? { right: 0, bottom: 0 };
      return { top: b.bottom, right: panel.right, bottom: Math.max(24, panel.bottom) };
    };
    this.stage = {
      canvas,
      hint,
      boardInsets,
      options: {
        onSelect: (id) => this.select(id),
        insets: () => {
          const panel = this.panel?.covered ?? { right: 0, bottom: 0 };
          // On narrow screens the brand card spans the full width, so keep the tree below it.
          const b = brandStack.getBoundingClientRect();
          const top = b.width > window.innerWidth * 0.6 ? b.bottom + 8 : 16;
          return { top, left: 16, right: panel.right, bottom: Math.max(88, panel.bottom) };
        },
        onTime: (ms) => {
          clear(time);
          if (ms === null) return;
          const d = new Date(ms);
          append(time, [h("span", { class: "brand-year" }, String(d.getFullYear())), h("span", { class: "brand-season" }, seasonLabel(seasonOf(d.toISOString())))]);
        },
      },
    };

    this.renderBrand();
    this.renderGenerations();
    this.createView();

    this.keyHandler = (e) => this.onKey(e);
    document.addEventListener("keydown", this.keyHandler);
  }

  private renderToolbar(): HTMLElement {
    const btn = (label: string, iconName: Parameters<typeof icon>[0], onClick: () => void, title?: string) =>
      h("button", { class: "btn", onclick: onClick, title: title ?? label, "aria-label": title ?? label }, icon(iconName, 15), h("span", { class: "btn-label" }, label));

    // A guest sees the same toolbar; only its last two items differ, because there is no account to
    // hang a menu off. Settings still need a button of their own there.
    const guestTail = !this.user
      ? [
          btn(t("toolbar.settings"), "gear", () => this.tree && this.openSettings(this.tree.repo)),
          h(
            "button",
            { class: "btn primary", onclick: () => this.showLogin(), title: t("guest.signInTitle") },
            icon("github", 15),
            h("span", { class: "btn-label" }, t("login.withGitHub")),
          ),
        ]
      : null;

    // The menu hangs off the account button on hover, so with a pointer the button itself is free to
    // do the obvious thing: go to the account's island. A touch screen has no hover, so there the tap
    // opens the menu instead, and the island is the first thing in it.
    const login = this.user?.login ?? "";
    const hovers = () => window.matchMedia("(hover: hover)").matches;
    const userEl = h(
      "div",
      { class: "user-menu" },
      h(
        "button",
        {
          class: "btn user-btn",
          title: t("menu.myIsland"),
          "aria-label": t("app.account"),
          "aria-haspopup": "menu",
          onclick: () => (hovers() ? this.openIsland(login) : userEl.classList.toggle("open")),
        },
        this.user?.avatar_url ? avatar(this.user.avatar_url, 22) : null,
        h("span", { class: "btn-label" }, `@${login}`),
      ),
      h(
        "div",
        { class: "card menu" },
        h("button", { class: "menu-item", onclick: () => this.openIsland(login) }, icon("island", 14), ` ${t("menu.myIsland")}`),
        this.tree
          ? h("button", { class: "menu-item", onclick: () => this.host.openExternal(`https://github.com/${this.tree!.repo}`) }, icon("github", 14), ` ${t("menu.openRepoOnGitHub")}`)
          : null,
        h("button", { class: "menu-item", onclick: () => this.showPicker() }, icon("repo", 14), ` ${t("menu.openOtherRepo")}`),
        h("hr", { class: "menu-sep" }),
        h("button", { class: "menu-item", onclick: () => this.host.openExternal(GUIDE_URL) }, icon("external", 14), ` ${t("menu.guide")}`),
        h("button", { class: "menu-item", onclick: () => this.host.openExternal(PROJECT_URL) }, icon("github", 14), ` ${t("menu.project")}`),
        h("hr", { class: "menu-sep" }),
        h("button", { class: "menu-item", onclick: () => this.tree && this.openSettings(this.tree.repo) }, icon("gear", 14), ` ${t("toolbar.settings")}`),
        h("button", { class: "menu-item", onclick: () => void this.signOut() }, icon("logout", 14), ` ${t("common.signOut")}`),
      ),
    );
    // A click leaves the focus on the button, and focus alone used to hold the menu open long after
    // the pointer had left it. Leaving closes it, and takes that focus with it.
    userEl.addEventListener("pointerleave", () => {
      userEl.classList.remove("open");
      if (userEl.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    });

    // One toolbar. The map is not a screen of its own: the same buttons stand there, and the ones
    // that need a tree simply have nothing to act on until the reader sails to a research.
    return h(
      "div",
      { class: "toolbar" },
      this.world?.map ? btn(t("world.map"), "island", () => this.openMap()) : null,
      btn(t("toolbar.refresh"), "refresh", () => void (this.tree ? this.refresh() : this.world && this.showWorld(this.world.user))),
      this.board ? null : this.modeButton(),
      this.boardButton(btn),
      this.host.capabilities.viewState ? btn(t("office.saveView"), "pin", () => void this.saveViewState(), t("office.saveViewTitle")) : null,
      this.board
        ? null
        : btn(t("toolbar.fit"), "fit", () => {
            this.activeDepth = null;
            this.renderGenerations();
            // On the sea, "fit" means the research being read, not every island at once.
            if (this.tree && this.worldScene) this.view?.sailTo(this.tree.repo);
            else this.view?.fit();
          }),
      guestTail ?? userEl,
    );
  }

  /** (Re)create the tree view for the current mode on the existing stage. */
  private createView(): void {
    const stage = this.stage;
    if (!stage) return;
    if (this.worldScene) return this.createWorldView(stage);
    if (!this.tree) return;
    this.view?.destroy();
    this.view = null;
    this.kanban?.destroy();
    this.kanban = null;
    stage.canvas.classList.toggle("is-board", this.board);
    if (this.board) {
      stage.hint.textContent = "";
      paintSystemBars(false);
      this.kanban = new Kanban(stage.canvas, { onSelect: (id) => this.select(id), insets: () => stage.boardInsets() });
      this.kanban.render(this.tree, this.filter());
      this.kanban.select(this.selected, false);
      return;
    }
    if (!Tree3D.supported()) {
      stage.hint.textContent = t("app.noWebGL");
      return;
    }
    stage.canvas.classList.toggle("is-3d", !isFlatMode(this.mode));
    stage.hint.textContent = hint(this.mode);
    this.view = new Tree3D(stage.canvas, stage.options, isFlatMode(this.mode), headingOf(this.mode));
    paintSystemBars(!isFlatMode(this.mode));
    this.view.render(this.tree, this.filter());
    this.view.select(this.selected, false);
    if (this.restoreCamera) {
      this.view.restoreCamera(this.restoreCamera);
      this.restoreCamera = null;
    }
  }

  /** The map of islands: one scene, every research the account shows, and no tree to walk yet. */
  private createWorldView(stage: { canvas: HTMLElement; hint: HTMLElement; options: ViewOptions }): void {
    this.view?.destroy();
    this.kanban?.destroy();
    this.kanban = null;
    stage.canvas.classList.remove("is-board");
    stage.canvas.classList.toggle("is-3d", !isFlatMode(this.mode));
    stage.hint.textContent = this.tree ? hint(this.mode) : t("hint.world");
    this.view = new Tree3D(
      stage.canvas,
      {
        onSelect: (id) => this.onWorldSelect(id),
        insets: () => stage.options.insets(),
        onTime: stage.options.onTime,
        onIsland: (repo) => this.onSailedOver(repo),
      },
      isFlatMode(this.mode),
      headingOf(this.mode),
    );
    paintSystemBars(!isFlatMode(this.mode));
    if (this.worldEntries.length) {
      this.view.renderWorld(this.sceneEntries());
      this.view.scopeLabels(this.tree?.repo ?? null);
      if (this.tree) this.view.sailTo(this.tree.repo);
      else this.view.fit(undefined, false);
    }
  }

  private boardButton(btn: (label: string, iconName: Parameters<typeof icon>[0], onClick: () => void, title?: string) => HTMLElement): HTMLElement {
    const button = btn(t(this.board ? "toolbar.boardOff" : "toolbar.board"), this.board ? "island" : "board", () => this.toggleBoard());
    // Both views are about one research; out on the open sea there is not one yet.
    (button as HTMLButtonElement).disabled = !this.tree;
    return button;
  }

  private modeButton(): HTMLElement {
    const label = () => t(MODE_ACTION[this.mode]);
    const text = h("span", { class: "btn-label" }, label());
    const button = h(
      "button",
      {
        class: "btn",
        title: t("toolbar.switchMode"),
        "aria-label": t("toolbar.switchMode"),
        disabled: !Tree3D.supported() || !this.tree,
        onclick: async () => {
          button.disabled = true;
          try {
            await this.switchMode();
          } catch (e) {
            this.toast(t("app.switchFailed", { error: errorText(e) }));
          } finally {
            text.textContent = label();
            button.disabled = false;
          }
        },
      },
      icon("cube", 15),
      text,
    );
    return button;
  }

  /**
   * Go to the next view. All four are the same scene: the islands are pressed flat under a top-down
   * camera (the ground disappears) or raised again, and the camera turns between the two headings.
   */
  private async switchMode(): Promise<void> {
    const stage = this.stage;
    const view = this.view;
    if (!stage || !view) return;
    const next = NEXT_MODE[this.mode];
    this.mode = next;
    this.host.storage.set(MODE_KEY, next);
    stage.hint.textContent = hint(next);
    // The sea comes back before the view rises out of the flat, and the neighbours leave only after
    // it has been pressed down — the swap happens at the flat end of the morph, where it barely
    // shows, and the press-down itself still plays over the whole scene.
    const swap = (entries: WorldEntry[]) => {
      if (!this.worldScene || !this.tree) return;
      // The camera stays exactly where the reader left it; only the islands around it change.
      view.renderWorld(entries, true);
      view.scopeLabels(this.tree.repo);
    };
    if (next === "island") swap(this.worldEntries);
    if (next === "flat") {
      await view.lower();
      stage.canvas.classList.remove("is-3d");
      paintSystemBars(false);
    } else if (next === "tree") {
      await view.turn("tree");
    } else if (next === "tree3d") {
      stage.canvas.classList.add("is-3d");
      paintSystemBars(true);
      await view.raise();
    } else {
      await view.turn("island");
    }
    if (next === "flat") {
      swap(this.sceneEntries());
      // The neighbours just left the sea; reframe on what is still there. The turning modes already
      // morphed around the research being read, so they need nothing here.
      if (this.tree && this.worldScene) view.sailTo(this.tree.repo);
    }
  }

  private renderBrand(): void {
    const brand = this.shell!.brand;
    clear(brand);
    if (this.isWorld) return this.renderWorldCard(brand);
    const tree = this.tree!;
    const [, name] = tree.repo.split("/");
    const nodes = [...tree.nodes.values()];
    const count = (s: Status) => nodes.filter((n) => n.status === s).length;
    const maxDepth = Math.max(0, ...nodes.map((n) => n.depth));

    const layers = h(
      "div",
      { class: "layers", role: "group", "aria-label": t("brand.layers") },
      STATUSES.map((s) =>
        h(
          "button",
          {
            class: `chip layer status-${s}`,
            "aria-pressed": String(!this.hidden.has(s)),
            title: t(this.hidden.has(s) ? "brand.showStatus" : "brand.dimStatus", { status: statusLabel(s) }),
            onclick: () => {
              if (this.hidden.has(s)) this.hidden.delete(s);
              else this.hidden.add(s);
              this.renderBrand();
              this.current?.render(this.tree!, this.filter());
              this.current?.select(this.selected, false);
            },
          },
          h("span", { class: "chip-dot" }),
          statusLabel(s),
          h("span", { class: "chip-count" }, count(s)),
        ),
      ),
    );

    // Open by default; collapsed it keeps only the count and the repo name (remembered).
    const collapsed = Boolean(this.host.storage.get<boolean>(BRAND_KEY));
    brand.classList.toggle("collapsed", collapsed);
    const toggle = h(
      "button",
      {
        class: "icon-btn brand-toggle",
        "aria-expanded": String(!collapsed),
        "aria-label": t(collapsed ? "brand.expand" : "brand.collapse"),
        title: t(collapsed ? "brand.expand" : "brand.collapse"),
        onclick: () => {
          this.host.storage.set(BRAND_KEY, !collapsed || undefined);
          this.renderBrand();
        },
      },
      icon("down", 14),
    );
    append(brand, [
      toggle,
      h("div", { class: "eyebrow" }, "Research Tree · DarkPyonix.dev"),
      h("div", { class: "brand-title" }, h("span", { class: "count" }, String(nodes.length).padStart(2, "0")), h("h1", null, name)),
      h(
        "p",
        { class: "brand-sub" },
        nodes.length
          ? tree.versions.size
            ? t("brand.subVersions", { root: tree.root, count: tree.versions.size + 1, depth: maxDepth })
            : t("brand.subBranches", { root: tree.root, count: tree.rootChildren.length, depth: maxDepth })
          : [t("brand.empty", { prefix: tree.prefix }), h("br"), t("brand.emptyNext", { root: tree.root })],
      ),
      layers,
      this.user ? null : h("p", { class: "muted small guest-note" }, t("guest.note"), h("br"), t("guest.noteDates")),
    ]);
  }

  private renderGenerations(): void {
    if (this.isWorld) {
      clear(this.shell!.gens);
      this.shell!.gens.hidden = true;
      return;
    }
    this.shell!.gens.hidden = false;
    const gens = this.shell!.gens;
    const tree = this.tree!;
    clear(gens);
    if (this.board) {
      gens.hidden = true;
      return;
    }
    const maxDepth = Math.max(0, ...[...tree.nodes.values()].map((n) => n.depth));
    if (maxDepth === 0) {
      gens.hidden = true;
      return;
    }
    gens.hidden = false;
    const go = (d: number | null) => {
      this.activeDepth = d;
      this.renderGenerations();
      if (d === null) this.view?.fit();
      else this.view?.fit([...tree.nodes.values()].filter((n) => n.depth === d).map((n) => n.id), true);
    };
    const cur = this.activeDepth;
    // Generations that don't fit scroll sideways (wheel or arrows); the active one is kept centered.
    const chips = Array.from({ length: maxDepth }, (_, i) => i + 1).map((d) =>
      h("button", { class: `gen-btn${cur === d ? " active" : ""}`, "aria-pressed": String(cur === d), onclick: () => go(d) }, String(d).padStart(2, "0")),
    );
    const track = h("div", { class: "gens-track" }, ...chips);
    track.addEventListener(
      "wheel",
      (e) => {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        track.scrollLeft += e.deltaY;
        e.preventDefault();
      },
      { passive: false },
    );
    gens.append(
      h("span", { class: "gens-label" }, t("gens.label")),
      h("button", { class: "icon-btn", "aria-label": t("gens.prev"), disabled: cur === null || cur <= 1, onclick: () => go(Math.max(1, (cur ?? 1) - 1)) }, icon("left")),
      track,
      h("button", { class: "icon-btn", "aria-label": t("gens.next"), disabled: cur === maxDepth, onclick: () => go(Math.min(maxDepth, (cur ?? 0) + 1)) }, icon("right")),
      h("span", { class: "gens-count" }, cur === null ? t("gens.all", { n: maxDepth }) : `${cur} / ${maxDepth}`),
    );
    const active = cur === null ? undefined : chips[cur - 1];
    if (active) requestAnimationFrame(() => (track.scrollLeft = active.offsetLeft - (track.clientWidth - active.offsetWidth) / 2));
  }

  private select(id: string | null, focus = true): void {
    const tree = this.tree;
    if (!tree || !this.panel || !this.current) return;
    // The intro reef is not a node: it opens the research documents and selects nothing.
    if (id === INTRO_ID) return this.showIntro(tree);
    const node = id ? tree.nodes.get(id) : undefined;
    const version = id ? tree.versions.get(id) : undefined;
    const isRoot = id === tree.root;
    this.selected = node?.id ?? version?.id ?? (isRoot ? tree.root : null);
    if (node) this.panel.show(node, tree);
    else if (version) this.panel.showVersion(version, tree);
    else if (isRoot) this.panel.showVersion(rootVersion(tree), tree);
    else this.panel.hide();
    this.shell?.brand.closest(".shell")?.classList.toggle("panel-open", Boolean(this.selected));
    this.current.select(this.worldScene && this.current === this.view ? this.viewId(this.selected) : this.selected, focus);
    // The panel takes room on the board rather than covering it, so the columns re-measure.
    this.kanban?.layout();
    this.setUrl({ repo: tree.repo, node: this.selected });
  }

  private onKey(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    if (target && (target.closest("input, textarea, select, [contenteditable]") || e.metaKey || e.ctrlKey || e.altKey)) return;
    const tree = this.tree;
    if (!tree) return;
    const cur = this.selected;
    const parent = cur ? parentOf(tree, cur) : undefined;
    const siblings = parent ? childrenOf(tree, parent) : tree.rootChildren;
    let next: string | null | undefined;

    switch (e.key) {
      case "Escape":
        next = null;
        break;
      case "ArrowRight":
        next = cur ? childrenOf(tree, cur)[0] : tree.rootChildren[0];
        break;
      case "ArrowLeft":
        next = cur && parent && parent !== tree.root ? parent : undefined;
        break;
      case "ArrowDown":
      case "ArrowUp": {
        if (!cur) {
          next = tree.rootChildren[0];
          break;
        }
        const i = siblings.indexOf(cur) + (e.key === "ArrowDown" ? 1 : -1);
        next = siblings[i];
        break;
      }
      default:
        return;
    }
    if (next === undefined) return;
    e.preventDefault();
    this.select(next);
    if (next) this.view?.focusNode(next);
  }

  /**
   * Write the address of what is on screen. `push` marks a real move — another island, another
   * research — so the browser's back button takes the reader where they came from; everything else
   * (picking a node, signing out) only rewrites the address of the place they are already in.
   */
  private setUrl(p: { repo: string | null; node: string | null; user?: string | null; push?: boolean }): void {
    if (this.host.kind === "extension") return;
    try {
      const params = new URLSearchParams(location.search);
      for (const k of ["code", "state", ...SETTING_PARAMS]) params.delete(k);
      // Every address names an account: the island it was opened from, or the repository's own owner.
      const user = p.user !== undefined ? p.user : (this.world?.user ?? p.repo?.split("/")[0] ?? null);
      if (user) params.set("user", user);
      else params.delete("user");
      const repo = shortRepo(user, p.repo);
      if (repo) params.set("repo", repo);
      else params.delete("repo");
      if (p.node) params.set("node", p.node);
      else params.delete("node");
      // Settings come after repo and node, so the link reads "what to open" first, "how to show it" after.
      if (p.repo) {
        const settings = settingParams({ locale: localePreference(this.host) });
        for (const [key, value] of Object.entries(settings)) if (value) params.set(key, value);
      }
      const q = params.toString().replace(/=(&|$)/g, "$1");
      const url = location.pathname + (q ? `?${q}` : "");
      // A move to the same place is no move: it replaces, so back does not have to be pressed twice.
      const was = readPlace(location.search);
      const moved = was.user !== user || was.repo !== fullRepo(user, repo);
      if (p.push && moved) history.pushState(null, "", url);
      else history.replaceState(null, "", url);
    } catch {
      /* Ignore environments where the URL cannot be changed. */
    }
  }

  private toastTimer = 0;
  private toast(msg: string): void {
    const el = this.shell?.toast;
    if (!el) return;
    el.textContent = msg;
    el.classList.add("show");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => el.classList.remove("show"), 2600);
  }
}
